import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Zap, ChevronLeft, TrendingUp, Smartphone } from "lucide-react";
import LiveScoresTicker from "@/components/LiveScoresTicker";

const StandingsPage = () => {
    const [leagues, setLeagues] = useState([]);
    const [activeLeague, setActiveLeague] = useState("eng.1");
    const [standings, setStandings] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(`${API}/live-standings`).then((r) => {
            setLeagues(r.data.leagues || []);
        });
    }, []);

    useEffect(() => {
        setLoading(true);
        axios
            .get(`${API}/live-standings/${activeLeague}`)
            .then((r) => setStandings(r.data))
            .catch(() => setStandings(null))
            .finally(() => setLoading(false));
    }, [activeLeague]);

    const activeLeagueMeta = leagues.find((l) => l.id === activeLeague) || {};

    return (
        <div className="min-h-screen bg-background">
            <LiveScoresTicker />

            {/* Header */}
            <header className="border-b border-border bg-paper sticky top-10 z-20">
                <div className="container-main py-3 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2" data-testid="back-home">
                        <Zap className="h-6 w-6 text-electric" />
                        <span className="dimbani-wordmark text-xl text-white">DIMBANI</span>
                    </Link>
                    <Link
                        to="/"
                        className="text-white/60 hover:text-white text-sm flex items-center gap-1"
                    >
                        <ChevronLeft className="h-4 w-4" />
                        Nyumbani
                    </Link>
                </div>
            </header>

            <main className="container-main py-6 space-y-6" data-testid="standings-page">
                {/* Title */}
                <div className="flex items-center gap-3 mb-4">
                    <TrendingUp className="h-7 w-7 text-electric" />
                    <div>
                        <h1 className="text-3xl sm:text-4xl font-heading font-black text-white uppercase tracking-tight">
                            Msimamo wa Ligi
                        </h1>
                        <p className="text-white/50 text-sm">
                            Msimamo wa ligi kuu duniani • Data ya moja kwa moja kutoka ESPN
                        </p>
                    </div>
                </div>

                {/* League Tabs */}
                <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-2 border-b border-border">
                    {leagues.map((lg) => (
                        <button
                            key={lg.id}
                            onClick={() => setActiveLeague(lg.id)}
                            data-testid={`league-tab-${lg.id}`}
                            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap border transition-colors flex items-center gap-2 ${
                                activeLeague === lg.id
                                    ? "bg-electric text-white border-electric"
                                    : "bg-subtle border-border text-white/70 hover:border-electric/40"
                            }`}
                        >
                            <span>{lg.flag}</span>
                            <span>{lg.name_sw || lg.name}</span>
                        </button>
                    ))}
                </div>

                {/* Standings Table */}
                <Card className="bg-paper border-border overflow-hidden">
                    {loading ? (
                        <div className="p-6 space-y-3">
                            {Array.from({ length: 10 }).map((_, i) => (
                                <Skeleton key={i} className="h-10 bg-subtle" />
                            ))}
                        </div>
                    ) : !standings || !standings.entries?.length ? (
                        <div className="p-12 text-center text-white/50">
                            Msimamo haupatikani kwa sasa.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="standings-table">
                                <thead className="bg-subtle border-b border-border">
                                    <tr className="text-left text-white/60 text-xs uppercase">
                                        <th className="px-3 py-3 w-12 text-center">#</th>
                                        <th className="px-3 py-3">Timu</th>
                                        <th className="px-3 py-3 text-center w-10" title="Mechi">MC</th>
                                        <th className="px-3 py-3 text-center w-10" title="Ushindi">U</th>
                                        <th className="px-3 py-3 text-center w-10" title="Sare">S</th>
                                        <th className="px-3 py-3 text-center w-10" title="Kushindwa">K</th>
                                        <th className="px-3 py-3 text-center w-14 hidden sm:table-cell" title="Mabao">MB</th>
                                        <th className="px-3 py-3 text-center w-14 hidden sm:table-cell" title="Tofauti">TF</th>
                                        <th className="px-3 py-3 text-center w-14">Pt</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {standings.entries.map((e, idx) => (
                                        <tr
                                            key={e.team_id || idx}
                                            className={`border-b border-border/40 hover:bg-subtle/50 ${
                                                idx < 4 ? "bg-electric/5" : idx >= standings.entries.length - 3 ? "bg-red-500/5" : ""
                                            }`}
                                            data-testid={`standings-row-${idx}`}
                                        >
                                            <td className="px-3 py-3 text-center">
                                                <Badge
                                                    className={`w-7 h-7 rounded-full flex items-center justify-center font-bold ${
                                                        idx < 4
                                                            ? "bg-electric text-white"
                                                            : idx >= standings.entries.length - 3
                                                            ? "bg-red-500/20 text-red-400 border border-red-500/30"
                                                            : "bg-subtle text-white/70 border border-border"
                                                    }`}
                                                >
                                                    {e.rank}
                                                </Badge>
                                            </td>
                                            <td className="px-3 py-3">
                                                <div className="flex items-center gap-3">
                                                    {e.team_logo && (
                                                        <img
                                                            src={e.team_logo}
                                                            alt=""
                                                            className="w-6 h-6 object-contain"
                                                        />
                                                    )}
                                                    <div>
                                                        <div className="text-white font-medium">{e.team_name}</div>
                                                        <div className="text-white/40 text-xs font-mono">{e.team_abbr}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-3 py-3 text-center text-white/80 font-mono">{e.played}</td>
                                            <td className="px-3 py-3 text-center text-electric font-mono">{e.wins}</td>
                                            <td className="px-3 py-3 text-center text-white/70 font-mono">{e.draws}</td>
                                            <td className="px-3 py-3 text-center text-white/70 font-mono">{e.losses}</td>
                                            <td className="px-3 py-3 text-center text-white/70 font-mono hidden sm:table-cell">
                                                {e.goals_for}:{e.goals_against}
                                            </td>
                                            <td className="px-3 py-3 text-center font-mono hidden sm:table-cell">
                                                <span className={e.goal_difference > 0 ? "text-electric" : e.goal_difference < 0 ? "text-red-400" : "text-white/60"}>
                                                    {e.goal_difference > 0 ? "+" : ""}{e.goal_difference}
                                                </span>
                                            </td>
                                            <td className="px-3 py-3 text-center text-white font-heading font-black text-lg">
                                                {e.points}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>

                {/* Legend + SMS CTA */}
                <div className="grid md:grid-cols-2 gap-4">
                    <Card className="bg-subtle border-border p-4">
                        <h3 className="text-white/80 font-heading font-bold text-sm uppercase tracking-wider mb-2">
                            Ufunguo
                        </h3>
                        <ul className="text-white/60 text-xs space-y-1">
                            <li><span className="text-electric">●</span> 1-4: Nafasi za Ligi ya Mabingwa</li>
                            <li><span className="text-red-400">●</span> 3 ya mwisho: Anashushwa daraja</li>
                            <li>MC = Mechi | U = Ushindi | S = Sare | K = Kushindwa | MB = Mabao | TF = Tofauti | Pt = Pointi</li>
                        </ul>
                    </Card>
                    <Card className="bg-gradient-to-br from-electric/10 to-subtle border-electric/30 p-4" data-testid="sms-standings-cta">
                        <div className="flex items-center gap-2 mb-2">
                            <Smartphone className="h-4 w-4 text-electric" />
                            <h3 className="text-white font-heading font-bold text-sm uppercase tracking-wider">
                                Pata Msimamo kwa SMS
                            </h3>
                        </div>
                        <p className="text-white/70 text-sm mb-2">
                            Tuma <span className="text-electric font-mono font-bold">MSIMAMO {activeLeagueMeta.code || "EPL"}</span> kwenda
                            <span className="text-electric font-mono font-bold"> 22244</span>
                        </p>
                        <p className="text-white/40 text-xs">
                            Utapokea majibu ya timu 5 za juu ndani ya SMS moja (≤160 herufi).
                        </p>
                    </Card>
                </div>
            </main>
        </div>
    );
};

export default StandingsPage;
